import { JobFilters } from 'api/jobs';
import { JobArchiveState, JobStatus, JobType } from 'interfaces/job';
import { compact, isBoolean, isEmpty, isNumber, isPlainObject, join, map, size } from 'lodash';

import { getJobTypeDisplayLabel } from 'components/Pages/Jobs/jobTypeLabels';
import { humanize } from 'utils/helpers';

export interface AppliedJobFilterChip {
  key: string;
  label: string;
}

const ignoredJobFilterKeys = new Set(['studyId', 'labId']);

const appliedJobFilterKeyOrder = [
  'types',
  'statuses',
  'secondaryAnalysisFilter',
  'includePipelines',
  'slideIds',
  'users',
  'senders',
  'jobIds',
  'jobName',
  'flowClassNames',
  'orchestrationIds',
  'primaryRunOrchestrationIds',
  'orchestrationIdTextFilter',
  'childTasksOnly',
  'attemptSelection',
  'archiveState',
];

const hasFilterValue = (value: unknown) => {
  if (isBoolean(value) || isNumber(value)) {
    return Boolean(value);
  }

  return !isEmpty(value);
};

const formatList = (values: unknown[], formatValue: (value: unknown) => string = (value) => String(value)) =>
  join(map(values, formatValue), ', ');

const formatUser = (user: unknown) => {
  if (isPlainObject(user)) {
    const record = user as Record<string, unknown>;
    return String(record.name || record.email || record.id || 'user');
  }

  return String(user);
};

const formatCount = (label: string, values: unknown[]) => {
  const count = size(values);
  return `${label}: ${count}`;
};

const chipForKey = (key: string, value: unknown): AppliedJobFilterChip | null => {
  if (ignoredJobFilterKeys.has(key) || !hasFilterValue(value)) {
    return null;
  }

  switch (key) {
    case 'types':
      return {
        key,
        label: `Job type: ${formatList(value as unknown[], (jobType) => getJobTypeDisplayLabel(jobType as JobType))}`,
      };
    case 'statuses':
      return {
        key,
        label: `Job status: ${formatList(value as unknown[], (status) => humanize(String(status as JobStatus)))}`,
      };
    case 'slideIds':
      return { key, label: formatCount('Job slides', value as unknown[]) };
    case 'users':
      return { key, label: `Submitted by: ${formatList(value as unknown[], formatUser)}` };
    case 'senders':
      return { key, label: `Source: ${formatList(value as unknown[])}` };
    case 'jobIds':
      return { key, label: formatCount('Job IDs', value as unknown[]) };
    case 'jobName':
      return { key, label: `Job name: ${value}` };
    case 'includePipelines':
      return { key, label: 'Include pipelines' };
    case 'flowClassNames':
      return {
        key,
        label: `Flow class: ${formatList(value as unknown[], (flowClass) => humanize(String(flowClass)))}`,
      };
    case 'orchestrationIds':
    case 'primaryRunOrchestrationIds':
      return { key, label: formatCount('Orchestration IDs', value as unknown[]) };
    case 'orchestrationIdTextFilter':
      return { key, label: `Orchestration ID: ${value}` };
    case 'attemptSelection':
      return { key, label: `Attempt: ${humanize(String(value))}` };
    case 'archiveState':
      return value === JobArchiveState.All ? null : { key, label: `Archive: ${humanize(String(value))}` };
    case 'secondaryAnalysisFilter':
      return { key, label: `Secondary analysis: ${humanize(String(value))}` };
    default:
      return { key, label: `${humanize(key)}: ${isPlainObject(value) ? humanize(key) : String(value)}` };
  }
};

// studyId is intentionally omitted. The procedures retriever replaces jobsFilters.studyId with the
// case study id before matching, so that field does not add a filter the Dataset table can show.
export const getAppliedJobFilterChips = (
  jobsFilters?: Partial<JobFilters> | null,
  jobsFiltersIncludeExclude?: string | null
): AppliedJobFilterChip[] => {
  const orderedKeys = [
    ...appliedJobFilterKeyOrder,
    ...Object.keys(jobsFilters || {}).filter((key) => !appliedJobFilterKeyOrder.includes(key)),
  ];
  const fieldChips = compact(orderedKeys.map((key) => chipForKey(key, jobsFilters?.[key as keyof JobFilters])));

  if (jobsFiltersIncludeExclude === 'exclude' && !isEmpty(fieldChips)) {
    return [{ key: 'jobsFiltersIncludeExclude', label: 'Job match: Exclude' }, ...fieldChips];
  }

  return fieldChips;
};

export const hasAppliedJobFilters = (
  jobsFilters?: Partial<JobFilters> | null,
  jobsFiltersIncludeExclude?: string | null
) => !isEmpty(getAppliedJobFilterChips(jobsFilters, jobsFiltersIncludeExclude));
